import { useMemo, useState } from "react";
import {
  Button,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from "web-design-system";
import {
  ArrowLeftIcon,
  CalculatorIcon,
  ChevronRightIcon,
  TrendingUpIcon,
  ArrowDownCircleIcon,
  XIcon,
  RepeatIcon,
  IndianRupeeIcon,
} from "lucide-react";
import { Calculators } from "web-core";
import { CalculatorBrandSection } from "./brand-section";

const partnerBrandRender = () => <CalculatorBrandSection />;

type CalcId = "lumpsum" | "sip-goal" | "swp" | "sip-returns" | "commissions";

interface CalcEntry {
  id: CalcId;
  title: string;
  description: string;
  icon: React.ReactNode;
  iconBg: string;
  calculator: React.FunctionComponent<{ brandRender: () => React.ReactNode }>;
}

const CALCULATORS: CalcEntry[] = [
  {
    id: "lumpsum",
    title: "One time",
    description: "Estimate the future value of a one-time investment.",
    icon: <TrendingUpIcon className="size-5" />,
    iconBg:
      "bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-400",
    calculator: Calculators.LumpsumCalculator,
  },
  {
    id: "sip-returns",
    title: "SIP",
    description: "Calculate the future value of monthly SIP investments.",
    icon: <RepeatIcon className="size-5" />,
    iconBg: "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400",
    calculator: Calculators.SIPReturnsCalculator,
  },
  {
    id: "sip-goal",
    title: "Goal SIP",
    description: "Find the monthly SIP needed to reach a target amount.",
    icon: <CalculatorIcon className="size-5" />,
    iconBg: "bg-pink-100 text-pink-600 dark:bg-pink-900/40 dark:text-pink-400",
    calculator: Calculators.GoalSIPCalculator,
  },
  {
    id: "swp",
    title: "SWP",
    description: "Plan regular withdrawals from your investment corpus.",
    icon: <ArrowDownCircleIcon className="size-5" />,
    iconBg:
      "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400",
    calculator: Calculators.SWPCalculator,
  },
  {
    id: "commissions",
    title: "Commissions",
    description: "Estimate earnings based on your SIP and AUM book.",
    icon: <IndianRupeeIcon className="size-5" />,
    iconBg:
      "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400",
    calculator: Calculators.CommissionsCalculator,
  },
];

export function Calculator() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCalc, setActiveCalc] = useState<CalcId | null>(null);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const currentCalc = useMemo(
    () => CALCULATORS.find((c) => c.id === activeCalc),
    [activeCalc]
  );

  const CalculatorComponent = useMemo(
    () => currentCalc?.calculator ?? null,
    [currentCalc]
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button size="icon" variant="ghost">
          <CalculatorIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-125 p-0 overflow-hidden data-[state=closed]:hidden"
        side="right"
        align="end"
        sideOffset={-40}
        alignOffset={-120}
        portal
        forceMount
      >
        <ScrollArea className="h-[calc(100vh-25px)]">
          <div className="flex flex-col min-h-full w-full">
            <div className="pt-4 sticky top-0 pb-3 flex bg-background items-center z-10 pl-6 pr-4">
              {activeCalc && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="mr-2 -ml-3 shrink-0"
                  onClick={() => setActiveCalc(null)}
                >
                  <ArrowLeftIcon className="size-4" />
                </Button>
              )}
              <span className="text-lg font-semibold mr-auto">
                {currentCalc ? currentCalc.title : "Calculators"}
              </span>
              <Button
                onClick={() => handleOpenChange(false)}
                size="icon"
                variant="ghost"
              >
                <XIcon />
              </Button>
            </div>

            <div className="p-6 pt-1 flex flex-col flex-1">
              {CalculatorComponent ? (
                <CalculatorComponent brandRender={partnerBrandRender} />
              ) : (
                <>
                  <div className="flex flex-col flex-1 gap-5">
                    {CALCULATORS.map((calc) => (
                      <button
                        key={calc.id}
                        onClick={() => setActiveCalc(calc.id)}
                        className="flex items-center cursor-pointer gap-4 w-full border border-secondary rounded-md bg-card hover:bg-muted/50 transition-colors p-4 text-left"
                      >
                        <div
                          className={cn(
                            "rounded-xl p-2.5 shrink-0",
                            calc.iconBg
                          )}
                        >
                          {calc.icon}
                        </div>
                        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                          <span className="text-sm font-semibold text-foreground">
                            {calc.title}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {calc.description}
                          </span>
                        </div>
                        <ChevronRightIcon className="size-4 text-muted-foreground shrink-0" />
                      </button>
                    ))}
                  </div>
                  <Calculators.ExpressionCalculator />
                </>
              )}
            </div>
          </div>
        </ScrollArea>
      </PopoverContent>
      {isOpen && (
        <div
          className={cn(
            "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
      )}
    </Popover>
  );
}
